import React, { useEffect, useState } from "react";
import { getPets, getRescues } from "../lib/api";
import { toast } from "sonner";
import { SectionHeading } from "../components/ArticleCard";
import { Heart, PawPrint, Building2 } from "lucide-react";

const SPECIES = ["All", "Dog", "Cat"];

export default function Pets() {
  const [pets, setPets] = useState([]);
  const [rescues, setRescues] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    getPets(filter).then(setPets).catch(() => {});
  }, [filter]);
  useEffect(() => { getRescues().then(setRescues).catch(() => {}); }, []);

  const featured = pets.find((p) => p.featured) || pets[0];
  const rest = pets.filter((p) => p.id !== featured?.id);

  return (
    <div className="paper-texture min-h-screen">
      <div className="border-b border-[var(--paper-border)] bg-[var(--navy)]">
        <div className="mx-auto max-w-7xl px-4 py-10">
          <p className="eyebrow flex items-center gap-2 text-[var(--gold)]"><PawPrint size={14} /> In partnership with local rescue groups</p>
          <h1 className="font-display text-4xl font-extrabold text-white sm:text-5xl">Adoptable Pets</h1>
          <p className="font-serif-body mt-2 max-w-2xl text-white/80">Meet the dogs and cats looking for their forever homes across Nassau County. Adopt, don&apos;t shop!</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10">
        {featured && (
          <div className="mb-12 grid gap-6 overflow-hidden rounded-2xl border border-[var(--gold)] bg-white ring-1 ring-[var(--gold)]/30 md:grid-cols-2" data-testid="featured-pet">
            <div className="img-zoom relative min-h-[320px] overflow-hidden">
              <img src={featured.image} alt={featured.name} className="h-full w-full object-cover object-top" />
              <span className="eyebrow absolute left-4 top-4 flex items-center gap-1 rounded-full bg-[var(--gold)] px-3 py-1 text-[10px] text-white"><Heart size={11} /> Pet of the Week</span>
            </div>
            <div className="flex flex-col justify-center p-8">
              <span className="eyebrow text-[var(--gold)]">{featured.species} · {featured.breed}</span>
              <h2 className="font-display mt-1 text-4xl font-extrabold text-[var(--navy)]">{featured.name}</h2>
              <p className="font-ui mt-1 text-sm text-slate-500">{featured.age} · {featured.sex} · {featured.status}</p>
              <p className="font-serif-body mt-4 text-lg leading-relaxed text-slate-700">{featured.description}</p>
              <p className="font-ui mt-4 flex items-center gap-1.5 text-sm text-slate-500"><Building2 size={14} className="text-[var(--gold)]" /> {featured.rescue}</p>
              <button onClick={() => toast.info(`To adopt ${featured.name}, contact ${featured.rescue} at (904) 261-7000 or adopt@${featured.rescue.toLowerCase().replace(/[^a-z]/g, "")}.org`)} className="font-ui mt-5 w-fit rounded-full bg-[var(--gold)] px-6 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48]" data-testid="adopt-featured">
                Inquire About {featured.name}
              </button>
            </div>
          </div>
        )}

        <div className="mb-6 flex flex-wrap gap-2">
          {SPECIES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`pet-filter-${s.toLowerCase()}`}
              className={`font-ui rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wider ${filter === s ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--paper-border)] bg-white text-slate-600 hover:border-[var(--navy)]"}`}>
              {s === "Dog" ? "Dogs" : s === "Cat" ? "Cats" : "All Pets"}
            </button>
          ))}
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {rest.map((p) => (
            <div key={p.id} data-testid={`pet-${p.id}`} className="card-hover overflow-hidden rounded-lg border border-[var(--paper-border)] bg-white">
              <div className="img-zoom h-56 overflow-hidden"><img src={p.image} alt={p.name} className="h-full w-full object-cover object-top" /></div>
              <div className="p-4">
                <span className="eyebrow text-[var(--gold)]">{p.species} · {p.breed}</span>
                <h3 className="font-display text-xl font-bold text-[var(--navy)]">{p.name}</h3>
                <p className="font-ui text-xs text-slate-500">{p.age} · {p.sex}</p>
                <p className="font-serif-body mt-2 line-clamp-2 text-sm text-slate-600">{p.description}</p>
                <p className="font-ui mt-2 flex items-center gap-1 text-xs text-slate-500"><Building2 size={12} className="text-[var(--gold)]" /> {p.rescue}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <SectionHeading title="Our Rescue Partners" tagline="List your adoptable animals free" />
          <div className="grid gap-4 sm:grid-cols-3">
            {rescues.map((r) => (
              <div key={r.id} className="flex items-center gap-3 rounded-lg border border-[var(--paper-border)] bg-white p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--gold)]/15 text-[var(--gold)]"><PawPrint size={20} /></span>
                <p className="font-display text-lg font-bold text-[var(--navy)]">{r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
